import React from 'react';
import { DetailCardType } from 'types/DetailPageTypes';
import { FieldType } from 'types/FieldTypes';
import { TabType } from 'types/TabTypes';
import DetailPage from 'components/layout/DetailPage';
import { useActiveFeature } from 'services/permissions/permissionChecks';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useFetchData } from 'services/api/useFetchData';
import { buildAdminOnlyContactTypeOptions, useAdminOnlyContactTypes } from './contactTypeOptions';
import { HeaderButtons } from 'types/ButtonTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faEllipsis } from '@fortawesome/free-solid-svg-icons';
import LocationForm from './LocationForm';

// Rename the name of the function:
const OrganizationDetail: React.FC = () => {

    // Specify the parameters of this detail page:
    const apiObject = 'contact'
    const pageObject = 'organization'
    const pageIcon = <FontAwesomeIcon icon={faBuilding} />

    // Define the header title fields:
    const headerTitleFields: FieldType[] = [
        {
            name: 'name',
            type: 'text',
            placeholder: 'organization.general.organization_name_placeholder',
            isEditable: { editability: false },
            saveOnBlur: true,
        },
    ]

    // Define the header buttons:
    const headerButtons: HeaderButtons = [
        {
            type: 'dropdown',
            icon: <FontAwesomeIcon icon={faEllipsis} />,
            tooltipText: 'button.more_button_label',
            size: 'medium',
            buttons: [
                {
                    label: 'button.merge_button_label',
                    action: 'merge',
                    allowedRight: ['can_delete'],
                },
                {
                    label: 'contact.convert.to_person_label',
                    action: 'convert_to_person',
                    allowedRight: ['can_delete'],
                    condition: [{ field: 'contact_type', notValues: ['supplier'] }],
                },
                {
                    label: 'general.delete',
                    action: 'delete',
                    onSuccess: 'redirect',
                    redirectTo: '/contacts',
                    danger: true,
                },
            ],
        },
    ]

    // Only show the tickets tab when the tickets feature is active
    const ticketsActive = useActiveFeature('tickets');

    // The cancelled type is only offered inside the admin environment
    const adminOnlyTypeOptions = buildAdminOnlyContactTypeOptions(useAdminOnlyContactTypes(), 'organization.contact_type');

    // The subscription fields of a customer are only shown to the platform staff in the admin environment
    const { environmentType, allowedRights } = useAuthContext();
    const showAdminCustomerFields = environmentType === 'admin' && (allowedRights.includes('no_user') || allowedRights.includes('staff_user'));

    // Fetch the packages for the manual package choice of a customer without subscription
    const { response: packageResponse } = useFetchData({ apiUrl: showAdminCustomerFields ? 'get_subscription_package_list' : '' });
    const packageOptions = (packageResponse?.results ?? []).map((subscriptionPackage: { key: string, name: string }) => ({
        value: subscriptionPackage.key,
        name: subscriptionPackage.name,
    }));

    // The read only subscription fields of a customer, their values come from the linked subscription
    const readOnly = { isEditable: { editability: false } };

    // Define the subscription fields shown below the custom fields for the platform staff
    const adminCustomerFields: FieldType[] = showAdminCustomerFields ? [
        {
            type: 'price',
            name: 'admin_mrr',
            label: 'contact.admin_customer.mrr_label',
            tooltipText: 'contact.admin_customer.mrr_tooltip',
            dataCondition: [{ field: 'admin_has_subscription', values: [false] }],
        },
        {
            type: 'price',
            name: 'admin_mrr',
            label: 'contact.admin_customer.mrr_label',
            tooltipText: 'contact.admin_customer.mrr_tooltip',
            dataCondition: [{ field: 'admin_has_subscription', values: [true] }],
            ...readOnly,
        },
        {
            type: 'dropdown',
            name: 'admin_subscription_status',
            label: 'contact.admin_customer.subscription_status_label',
            allowNoneOption: false,
            showSearch: false,
            hardcodedOptions: [
                { value: 'active', name: 'contact.admin_subscription_status.active' },
                { value: 'ending', name: 'contact.admin_subscription_status.ending' },
                { value: 'cancelled', name: 'contact.admin_subscription_status.cancelled' },
                { value: 'expired', name: 'contact.admin_subscription_status.expired' },
                { value: 'suspended', name: 'contact.admin_subscription_status.suspended' },
            ],
            ...readOnly,
        },
        {
            type: 'dropdown',
            name: 'admin_duration',
            label: 'contact.admin_customer.duration_label',
            allowNoneOption: false,
            showSearch: false,
            hardcodedOptions: [
                { value: 'monthly', name: 'contact.admin_duration.monthly' },
                { value: 'yearly', name: 'contact.admin_duration.yearly' },
            ],
            ...readOnly,
        },
        {
            type: 'date',
            name: 'admin_period_start',
            label: 'contact.admin_customer.period_start_label',
            ...readOnly,
        },
        {
            type: 'date',
            name: 'admin_period_end',
            label: 'contact.admin_customer.period_end_label',
            ...readOnly,
        },
        {
            type: 'dropdown',
            name: 'admin_package',
            label: 'contact.admin_customer.package_label',
            tooltipText: 'contact.admin_customer.package_tooltip',
            disabled_selected: 'contact.admin_customer.package_disabled_selected',
            allowNoneOption: true,
            showSearch: false,
            hardcodedOptions: packageOptions,
            dataCondition: [{ field: 'admin_has_subscription', values: [false] }],
        },
        {
            type: 'text',
            name: 'admin_package_name',
            label: 'contact.admin_customer.package_label',
            placeholder: 'contact.admin_customer.package_label',
            tooltipText: 'contact.admin_customer.package_tooltip',
            dataCondition: [{ field: 'admin_has_subscription', values: [true] }],
            ...readOnly,
        },
        {
            type: 'number',
            name: 'admin_seats',
            label: 'contact.admin_customer.seats_label',
            ...readOnly,
        },
        {
            type: 'text',
            name: 'admin_integrations',
            label: 'contact.admin_customer.integrations_label',
            placeholder: 'contact.admin_customer.integrations_label',
            ...readOnly,
        },
    ] : [];

    // Define the detail page tabs:
    const tabs: TabType[] = [ 
        { label: 'timeline.general.timeline_label', type: 'timeline', showOnlyTimelineUpdates: false },
        ...(ticketsActive ? [{ label: 'ticket.general.tickets_label', type: 'tickets' }] : []),
        { label: 'proposal.general.proposals_label', type: 'proposals' },
        { label: 'job.general.jobs_label', type: 'jobs' },
        { label: 'invoice.general.invoices_label', type: 'invoices' },
        { label: 'document.general.documents_label', type: 'attachments' },
    ]

    // Define detail components and add fields to FieldGroup cards:
    const detailCards: DetailCardType[] = [
        {
            component: 'FieldsCard',
            title: 'organization.general.general_details_card_label',
            alignment: 'horizontal',
            fields: [
                { 
                    type: 'text', 
                    name: 'number', 
                    label: 'organization.general.number_label', 
                    placeholder: 'organization.general.number_placeholder', 
                },
                { 
                    type: 'text', 
                    name: 'reference', 
                    label: 'organization.general.reference_label', 
                    placeholder: 'organization.general.reference_placeholder', 
                    tooltipText: 'organization.general.reference_tooltip'
                },
                { 
                    type: 'dropdown', 
                    name: 'contact_type', 
                    label: 'organization.general.type_label', 
                    disabled_selected: 'forms.select_type_disabled_selected',
                    defaultValue: { value: 'client' },
                    allowNoneOption: false,
                    showSearch: false,
                    hardcodedOptions: [
                        { value: 'contact', name: 'organization.contact_type.contact'},
                        { value: 'lead', name: 'organization.contact_type.lead'},
                        { value: 'client', name: 'organization.contact_type.client'},
                        ...adminOnlyTypeOptions,
                        { value: 'supplier', name: 'organization.contact_type.supplier'},
                    ],
                },
                {
                    type: 'switch',
                    name: 'blocked',
                    label: 'contact.general.blocked_label',
                    tooltipText: 'contact.general.blocked_tooltip',
                    onValue: 'forms.true_value',
                    offValue: 'forms.false_value',
                },
                { 
                    type: 'multiselect', 
                    name: 'labels', 
                    label: 'organization.general.labels_label', 
                    apiObject: 'label',
                    apiRequest: [{
                        endpoint: 'get_label_list',
                        object: 'label',
                        params: { deleted: false, connected_feature: 'organizations' }
                    }],
                    allowNoneOption: false,
                    showSearch: false,
                    disabled_selected: 'organization.general.labels_disabled_selected',
                },
                {
                    type: 'custom-fields',
                    name: 'custom_fields',
                },
                ...adminCustomerFields,
                { 
                    name: 'primary_locations',
                    type: 'locations', 
                    label: 'organization.general.address_label',
                    apiField: 'primary_locations',
                    addNewItem: { 
                        modalForm: <LocationForm />, 
                        linkedObject: 'primary_locations' 
                    }
                },
                { 
                    name: 'phone_numbers',
                    type: 'phonenumbers', 
                },
                { 
                    name: 'email_addresses',
                    type: 'emailaddresses', 
                },
            ]
        },
        {
            component: 'FieldsCard',
            title: 'contact.general.internal_note_label',
            alignment: 'stacked',
            fields: [
                {
                    name: 'internal_note',
                    type: 'textarea',
                    placeholder: 'contact.general.notes_placeholder',
                    isNote: true,
                },
            ]
        },
        {
            component: 'PeopleCard',
            apiField: 'primary_persons',
        },
        {
            component: 'LocationsCard',
        },
        {
            component: 'FieldsCard',
            title: 'organization.general.financial_details_card_label',
            alignment: 'horizontal',
            fields: [
                {
                    type: 'text', 
                    name: 'coc_number', 
                    label: 'organization.general.coc_number_label', 
                    placeholder: 'organization.general.coc_number_placeholder', 
                },
                {
                    type: 'text', 
                    name: 'vat_number', 
                    label: 'organization.general.vat_number_label', 
                    placeholder: 'organization.general.vat_number_placeholder', 
                },
                { 
                    type: 'tabs', 
                    name: 'vat_reverse_charge', 
                    label: 'organization.general.vat_reverse_charge_label', 
                    tooltipText: 'organization.general.vat_reverse_charge_tooltipText',
                },
                {
                    type: 'text', 
                    name: 'iban_number', 
                    label: 'organization.general.iban_number_label', 
                    placeholder: 'organization.general.iban_number_placeholder', 
                },
            ],
        },
        {
            component: 'RelatedOrganizationsCard',
        },
    ]

    return (
        <DetailPage
            apiObject={apiObject}
            pageObject={pageObject}
            pageIcon={pageIcon}
            headerTitleFields={headerTitleFields}
            headerButtons={headerButtons}
            detailCards={detailCards}
            tabs={tabs}
        />
    );
};

export default OrganizationDetail;